import React, { useContext } from 'react';
import styled from 'styled-components';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import { useSpring, animated } from 'react-spring';
import {
  PageListingFiltersFragment,
  PropertiesListingConfigFragment,
  PropertySpecFragment,
} from '../../../gatsby-graphql';
import FilterSelect from '../FilterSelect/FilterSelect';
import {
  getSelectOptions,
  updatePropertyLocationInPath,
  updatePropertyTypeInPath,
} from '../../utils';
import PriceSlider from '../PriceSlider/PriceSlider';
import NumberSelector from '../NumberSelector/NumberSelector';
import PropertiesFiltersContext from '../../context/PropertiesFiltersContext';
import PropertySpecSelector from '../PropertySpecSelector/PropertySpecSelector';
import Button from '../Button/Button';
import OperationTypesSelector from './OperationTypesSelector';

interface IPropertiesFiltersProps {
  config: PropertiesListingConfigFragment;
  propertyLocations: PageListingFiltersFragment['propertyLocations'];
  activePropertyType?: { label: string; value: string };
  activePropertyLocationType?: { label: string; value: string };
  localizedFilters?: boolean;
  propertySpecs: PropertySpecFragment[];
}

const PropertiesFilters: React.FC<IPropertiesFiltersProps> = ({
  config,
  propertyLocations,
  activePropertyType,
  activePropertyLocationType,
  localizedFilters,
  propertySpecs,
}) => {
  const location = useLocation();
  const {
    priceDeltas,
    priceValues,
    setPriceValues,
    setBedroomCount,
    setBathroomCount,
    setGaragesCount,
    handlePropertySpecChange,
    filterPropertiesLength,
    isDrawerOpen,
    setIsDrawerOpen,
    isTouched,
  } = useContext(PropertiesFiltersContext);
  const drawerProps = useSpring({
    transform: isDrawerOpen ? 'translateX(0)' : 'translateX(100%)',
  });
  const parsedPropertyLocations = [
    { value: 'reset', label: 'Todas' },
    ...getSelectOptions(propertyLocations.nodes),
  ];
  const parsedPropertyTypes = [
    { value: 'reset', label: 'Todos' },
    ...getSelectOptions(config.propertyTypes),
  ];
  return (
    <Container style={drawerProps}>
      <MobileControls>
        <span>{config.mobileTitle}</span>
        <Button
          onClick={() => setIsDrawerOpen((prevValue) => !prevValue)}
          label={
            isTouched
              ? (config.mobileAcceptFilterLabel as string)
              : (config.mobileCloseFilterLabel as string)
          }
          color="primary"
          buttonStyle="solid"
        />
      </MobileControls>
      <OperationTypesSelector operationTypes={config.operationTypes} />
      <FiltersContainer>
        <FilterTitle>{config.propertyLocationLabel}</FilterTitle>
        <FilterSelect
          isSearchable
          placeholder={config.propertyLocationPlaceholder}
          assistiveLabel={config.propertyLocationLabel}
          options={parsedPropertyLocations}
          onChange={(option) =>
            navigate(
              updatePropertyLocationInPath(
                location.pathname,
                option?.value,
                localizedFilters
              )
            )
          }
          defaultValue={activePropertyLocationType}
        />
        <FilterTitle>{config.propertyTypeLabel}</FilterTitle>
        <FilterSelect
          placeholder={config.propertyTypePlaceholder}
          assistiveLabel={config.propertyTypeLabel}
          options={parsedPropertyTypes}
          onChange={(option) =>
            navigate(
              updatePropertyTypeInPath(
                location.pathname,
                option?.value,
                localizedFilters
              )
            )
          }
          defaultValue={activePropertyType}
        />
        {filterPropertiesLength > 1 && (
          <>
            <FilterTitle>{config.priceLabel}</FilterTitle>
            <PriceSlider {...{ priceDeltas, priceValues, setPriceValues }} />
          </>
        )}
        <FilterTitle>{config.roomsLabel}</FilterTitle>
        <FilterSubtitle>{config.bedroomsLabel}</FilterSubtitle>
        <NumberSelector onChange={setBedroomCount} />
        <FilterSubtitle>{config.bathroomsLabel}</FilterSubtitle>
        <NumberSelector onChange={setBathroomCount} />
        <FilterTitle>{config.parkingLabel}</FilterTitle>
        <NumberSelector onChange={setGaragesCount} />
        <FilterTitle>{config.amenitiesLabel}</FilterTitle>
        <PropertySpecSelector
          {...{ propertySpecs, handlePropertySpecChange }}
        />
      </FiltersContainer>
    </Container>
  );
};

const Container = styled(animated.div)`
  ${(props) => props.theme.mq.untilDesktop} {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background-color: ${(props) => props.theme.colors.white};
    height: 100vh;
    z-index: 1;
    overflow-y: scroll;
    padding-bottom: 160px;
  }
  ${(props) => props.theme.mq.desktop} {
    transform: translateX(0) !important;
  }
`;

const MobileControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 16px 28px;
  & > span:first-of-type {
    font-size: 16px;
    line-height: 19px;
    font-weight: bold;
    color: ${(props) => props.theme.colors.dark.primary};
  }
  button {
    font-size: 12px;
    line-height: 15px;
    padding: 6px 14px;
  }
  ${(props) => props.theme.mq.desktop} {
    display: none;
  }
`;

const FiltersContainer = styled.div`
  border-radius: 8px;
  padding: 24px 16px 34px;
  ${(props) => props.theme.mq.desktop} {
    margin-top: 30px;
    box-shadow: 0 2px 10px 2px rgba(0, 0, 0, 0.1);
  }
`;

const FilterTitle = styled.span`
  display: block;
  color: ${(props) => props.theme.colors.dark.primary};
  font-size: 14px;
  line-height: 17px;
  font-weight: 600;
  letter-spacing: 0;
  margin: 0 0 12px;
  &:not(:first-of-type) {
    margin-top: 26px;
  }
  ${(props) => props.theme.mq.desktop} {
    font-size: 16px;
    line-height: 19px;
  }
`;

const FilterSubtitle = styled.span`
  display: block;
  font-size: 12px;
  line-height: 14px;
  margin: 8px 0 6px;
  font-weight: 500;
  color: #8b8c8f;
`;

export default React.memo(PropertiesFilters);
